import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { planApi } from '../api/tenants.js';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card } from '../components/ui/Card.jsx';
import {
    Layers, Check, X, Phone, BookOpen, Cpu, Clock,
    ClipboardList, PhoneOutgoing, Infinity as InfinityIcon,
} from 'lucide-react';

// 方案名稱依 plan.id 翻譯（後端回傳的 name 是硬編中文）；查無對照退回原文
const planLabel = (p, t) => (p ? t('name.' + (p.id || p.planId), { defaultValue: p.planName || p.name || '' }) : '');

// 價格跟介面語系的國家走（美金為定價基準 × 匯率換算成當地幣）：
// 日本用戶看日幣、韓國看韓元 —— 沒有人會想看美金定價。
// 後端 listPlans/me-plan 回 prices = { USD|TWD|JPY|...: { seatMonthly, setupFee, overagePerMin } }
const CURRENCY_BY_LANG = {
    'zh-TW': 'TWD', 'zh-CN': 'CNY', 'ja': 'JPY', 'ko': 'KRW',
    'vi': 'VND', 'th': 'THB', 'ms': 'MYR', 'en': 'USD',
};
const currencyForLang = (lang) => {
    if (!lang) return 'USD';
    if (CURRENCY_BY_LANG[lang]) return CURRENCY_BY_LANG[lang];
    const base = lang.split('-')[0];
    if (CURRENCY_BY_LANG[base]) return CURRENCY_BY_LANG[base];
    if (lang.startsWith('zh')) return 'TWD';
    return 'USD';
};
const CUR_SYMBOL = { TWD: 'NT$', CNY: '¥', JPY: '¥', KRW: '₩', VND: '₫', THB: '฿', MYR: 'RM', USD: '$' };
const planPrice = (p, cur, key) => p?.prices?.[cur]?.[key];

/**
 * Permission table — 三 plan 對照
 *
 * 純顯示，從後端 /api/ivr/admin/plans 拉資料。
 * 目前 capability 跟配額僅作參考；runtime enforcement 尚未啟用（之後再開）。
 */

const CAPABILITY_ROWS = [
    { key: 'transferRouting', icon: Phone, labelKey: 'capability.transferRouting.label', descKey: 'capability.transferRouting.desc' },
    { key: 'knowledgeBase', icon: BookOpen, labelKey: 'capability.knowledgeBase.label', descKey: 'capability.knowledgeBase.desc' },
    { key: 'formCollection', icon: ClipboardList, labelKey: 'capability.formCollection.label', descKey: 'capability.formCollection.desc' },
    { key: 'apiTools', icon: Cpu, labelKey: 'capability.apiTools.label', descKey: 'capability.apiTools.desc' },
    { key: 'outboundCampaign', icon: PhoneOutgoing, labelKey: 'capability.outboundCampaign.label', descKey: 'capability.outboundCampaign.desc' },
];

const NODE_TYPE_LABEL_KEYS = {
    router: 'nodeType.router',
    transfer: 'nodeType.transfer',
    end: 'nodeType.end',
    knowledge: 'nodeType.knowledge',
    formCollection: 'nodeType.formCollection',
    apiTool: 'nodeType.apiTool',
    auth: 'nodeType.auth',
    productRecommend: 'nodeType.productRecommend',
};

export default function PlansPage() {
    const { t } = useTranslation('plans');
    const [plans, setPlans] = useState(null);
    const [myPlan, setMyPlan] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([planApi.list(), planApi.me().catch(() => null)])
            .then(([list, me]) => {
                if (cancelled) return;
                setPlans(list.plans || []);
                setMyPlan(me?.plan || null);
            })
            .catch((e) => { if (!cancelled) setError(e); });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="plans-stage">
            <PageHeader
                eyebrow="plans · capabilities"
                title={t('title')}
                description={t('description')}
            />

            {error && (
                <div className="mb-6 px-4 py-3 rounded-md border border-danger-500/30 bg-danger-100/40 text-sm text-danger-500">
                    {t('error.loadFailed', { message: error.message })}
                </div>
            )}

            {!plans ? (
                <div className="text-center py-20 text-ink-400 font-mono text-xs">{t('loading')}</div>
            ) : (
                <>
                    {myPlan && <CurrentPlanBanner plan={myPlan} />}
                    <PlanComparisonTable plans={plans} myPlanId={myPlan?.id} />
                    <PricingFooter plans={plans} />
                </>
            )}

            <PlansStyles />
        </div>
    );
}

function CurrentPlanBanner({ plan }) {
    const { t, i18n } = useTranslation('plans');
    const cur = currencyForLang(i18n.language);
    const overage = planPrice(plan, cur, 'overagePerMin') ?? plan.overageRateUsdPerMin;
    return (
        <Card className="mb-8 reveal" padded={false}>
            <div className="px-7 py-5 flex items-center gap-5 flex-wrap">
                <div className="flex items-center gap-3">
                    <span className="size-2 rounded-full bg-accent-500" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
                        your plan
                    </span>
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="heading-display text-2xl font-semibold text-ink-900 tracking-tight">
                        {planLabel(plan, t)}
                    </span>
                    <span className="font-mono text-[10px] text-ink-400 uppercase tracking-[0.2em]">
                        {plan.id}
                    </span>
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-5 tnum text-sm">
                    <span className="text-ink-500">
                        <Clock size={12} className="inline mr-1 -mt-0.5" />
                        {plan.monthlyQuotaMinutes == null ? t('unlimited') : t('banner.minutesPerMonth', { n: plan.monthlyQuotaMinutes })}
                    </span>
                    {plan.monthlyQuotaMinutes != null && (
                        <span className="text-ink-500">
                            {t('banner.overagePerMin', { rate: `${CUR_SYMBOL[cur]} ${overage}` })}
                        </span>
                    )}
                </div>
            </div>
        </Card>
    );
}

function PlanComparisonTable({ plans, myPlanId }) {
    const { t, i18n } = useTranslation('plans');
    const cur = currencyForLang(i18n.language);
    const sym = CUR_SYMBOL[cur];
    return (
        <Card padded={false} className="reveal overflow-hidden" style={{ animationDelay: '120ms' }}>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-cream-200">
                            <th className="text-left font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 px-6 py-4 w-[26%]">
                                {t('table.capabilityQuota')}
                            </th>
                            {plans.map((p) => (
                                <th
                                    key={p.id}
                                    className={[
                                        'text-left px-6 py-4 align-bottom',
                                        p.id === myPlanId ? 'bg-accent-50/60' : '',
                                    ].join(' ')}
                                >
                                    <div className="flex items-baseline justify-between gap-3">
                                        <div>
                                            <div className="heading-display text-lg font-semibold text-ink-900 tracking-tight">
                                                {planLabel(p, t)}
                                            </div>
                                            <div className="font-mono text-[10px] text-ink-400 uppercase tracking-[0.18em] mt-1">
                                                {p.id}
                                            </div>
                                        </div>
                                        {p.id === myPlanId && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-cream-50 text-[10px] font-medium uppercase tracking-[0.15em] whitespace-nowrap">
                                                current
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <SectionRow label={t('section.billing')} />
                        <DataRow
                            label={t('row.monthlyFee.label')}
                            sub={t('row.monthlyFee.sub')}
                            plans={plans}
                            myPlanId={myPlanId}
                            render={(p) => (
                                <span className="tnum text-base font-semibold text-ink-900">
                                    {sym} {(planPrice(p, cur, 'seatMonthly') ?? p.priceMonthlyTwd).toLocaleString()}
                                </span>
                            )}
                        />
                        <DataRow
                            label={t('row.freeMinutes.label')}
                            sub={t('row.freeMinutes.sub')}
                            plans={plans}
                            myPlanId={myPlanId}
                            render={(p) => p.monthlyQuotaMinutes == null ? (
                                <span className="inline-flex items-center gap-1.5 tnum">
                                    <InfinityIcon size={18} strokeWidth={2} className="text-accent-500" />
                                    <span className="text-base font-semibold text-ink-900">{t('unlimited')}</span>
                                </span>
                            ) : (
                                <span className="tnum text-base font-semibold text-ink-900">
                                    {p.monthlyQuotaMinutes.toLocaleString()}
                                    <span className="text-ink-400 font-mono text-[10px] ml-1.5 uppercase tracking-[0.15em]">min</span>
                                </span>
                            )}
                        />
                        <DataRow
                            label={t('row.overageRate.label')}
                            sub={t('row.overageRate.sub')}
                            plans={plans}
                            myPlanId={myPlanId}
                            render={(p) => p.monthlyQuotaMinutes == null ? (
                                <span className="text-ink-300 text-sm">—</span>
                            ) : (
                                <span className="tnum text-base font-semibold text-ink-900">
                                    {/* 美金定價 × 匯率 → 依介面語系顯示當地幣 */}
                                    {sym} {(planPrice(p, cur, 'overagePerMin') ?? p.overageRatePerMinTwd ?? p.overageRateUsdPerMin).toLocaleString()}
                                    <span className="text-ink-400 font-mono text-[10px] ml-1.5 uppercase tracking-[0.15em]">/ min</span>
                                </span>
                            )}
                        />

                        <SectionRow label={t('section.capabilities')} />
                        {CAPABILITY_ROWS.map((cap) => (
                            <DataRow
                                key={cap.key}
                                label={t(cap.labelKey)}
                                sub={t(cap.descKey)}
                                icon={cap.icon}
                                plans={plans}
                                myPlanId={myPlanId}
                                render={(p) => <BoolCell on={!!(p.capabilities && p.capabilities[cap.key])} />}
                            />
                        ))}

                        <SectionRow label={t('section.nodeTypes')} />
                        <DataRow
                            label={t('row.flowNodes.label')}
                            sub={t('row.flowNodes.sub')}
                            plans={plans}
                            myPlanId={myPlanId}
                            render={(p) => <NodeTypeChips types={p.capabilities?.allowedNodeTypes || []} />}
                        />
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function SectionRow({ label }) {
    return (
        <tr className="border-b border-cream-100">
            <td colSpan={4} className="px-6 py-3 bg-cream-50/60">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">
                    {label}
                </span>
            </td>
        </tr>
    );
}

function DataRow({ label, sub, icon: Icon, plans, myPlanId, render }) {
    return (
        <tr className="border-b last:border-0 border-cream-100 hover:bg-cream-50/40 transition">
            <td className="px-6 py-4 align-top">
                <div className="flex items-start gap-2.5">
                    {Icon && <Icon size={14} strokeWidth={1.8} className="text-ink-400 mt-0.5 shrink-0" />}
                    <div>
                        <div className="text-sm font-medium text-ink-900">{label}</div>
                        {sub && <div className="text-[11px] text-ink-400 mt-0.5">{sub}</div>}
                    </div>
                </div>
            </td>
            {plans.map((p) => (
                <td
                    key={p.id}
                    className={[
                        'px-6 py-4 align-top',
                        p.id === myPlanId ? 'bg-accent-50/40' : '',
                    ].join(' ')}
                >
                    {render(p)}
                </td>
            ))}
        </tr>
    );
}

function BoolCell({ on }) {
    return on ? (
        <span className="inline-flex items-center justify-center size-7 rounded-full bg-success-100">
            <Check size={14} strokeWidth={2.4} className="text-success-500" />
        </span>
    ) : (
        <span className="inline-flex items-center justify-center size-7 rounded-full bg-cream-100">
            <X size={14} strokeWidth={2} className="text-ink-300" />
        </span>
    );
}

function NodeTypeChips({ types }) {
    const { t } = useTranslation('plans');
    if (!types || types.length === 0) {
        return <span className="text-ink-300 text-sm">—</span>;
    }
    return (
        <div className="flex flex-wrap gap-1.5">
            {types.map((type) => (
                <span
                    key={type}
                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-cream-100 text-ink-700 text-[10px] font-medium tracking-tight"
                    title={type}
                >
                    {NODE_TYPE_LABEL_KEYS[type] ? t(NODE_TYPE_LABEL_KEYS[type]) : type}
                </span>
            ))}
        </div>
    );
}

function PricingFooter({ plans }) {
    const { t } = useTranslation('plans');
    return (
        <Card className="mt-8 reveal" style={{ animationDelay: '220ms' }}>
            <div className="flex items-start gap-3">
                <Layers size={16} strokeWidth={1.6} className="text-accent-500 shrink-0 mt-0.5" />
                <div className="text-xs text-ink-500 leading-relaxed">
                    <span className="text-ink-700 font-medium">{t('footer.title')}</span>
                    <span className="text-ink-400"> · </span>
                    {t('footer.setupFee')}
                    <span className="text-ink-400"> · </span>
                    {t('footer.monthlyFee')}
                    <span className="text-ink-400"> · </span>
                    {t('footer.overage')}
                    <span className="block mt-2 text-ink-400 italic">
                        {t('footer.note', { n: plans.length })}
                    </span>
                </div>
            </div>
        </Card>
    );
}

function PlansStyles() {
    return (
        <style>{`
            .plans-stage .reveal {
                animation: plansReveal 520ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
            }
            @keyframes plansReveal {
                from { opacity: 0; transform: translateY(8px); }
                to   { opacity: 1; transform: translateY(0); }
            }
        `}</style>
    );
}
